import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Skeleton,
  Chip,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Alert,
  Tooltip,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import GroupIcon from "@mui/icons-material/Group";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import GroupsIcon from "@mui/icons-material/Groups";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

/* -------------------- STAT CARD -------------------- */
const StatCard = ({ label, value, icon, color, link, loading, sub }) => (
  <Card
    component={link ? RouterLink : "div"}
    to={link || undefined}
    sx={{
      textDecoration: "none",
      height: "100%",
      position: "relative",
      overflow: "hidden",
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "#fff",
      boxShadow: "none",
      transition: "all 0.25s ease",
      cursor: link ? "pointer" : "default",
      "&:hover": link
        ? {
            transform: "translateY(-3px)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
            borderColor: `${color}.main`,
          }
        : {},
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        bgcolor: `${color}.main`,
      },
    }}
  >
    <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        <Avatar
          sx={{
            bgcolor: (t) =>
              t.palette.mode === "light" ? `${color}.50` : `${color}.900`,
            color: `${color}.main`,
            width: 40,
            height: 40,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontSize: 10,
              display: "block",
            }}
          >
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={50} height={30} />
          ) : (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                lineHeight: 1.2,
                mt: 0.25,
              }}
            >
              {value ?? 0}
            </Typography>
          )}
          {sub && !loading && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10 }}
            >
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

/* -------------------- CARD SETS BY ROLE -------------------- */
const cardsByRole = {
  Admin: (s) => [
    { label: "Clients", value: s.total_clients, icon: <PeopleIcon fontSize="small" />, color: "primary", link: "/clients" },
    { label: "Pending Jobs", value: s.pending_jobs, icon: <WorkIcon fontSize="small" />, color: "warning", link: "/jobs" },
    { label: "Active Work", value: s.pending_tasks, icon: <AssignmentIcon fontSize="small" />, color: "info", link: "/my-desk" },
    { label: "Services", value: s.total_services, icon: <ListAltIcon fontSize="small" />, color: "success", link: "/services" },
    { label: "Users", value: s.total_users, icon: <GroupIcon fontSize="small" />, color: "secondary", link: "/user" },
  ],
  BDM: (s) => [
    { label: "Clients", value: s.total_clients, icon: <PeopleIcon fontSize="small" />, color: "primary", link: "/clients" },
    { label: "Pending Jobs", value: s.pending_jobs, icon: <WorkIcon fontSize="small" />, color: "warning", link: "/jobs" },
    { label: "Active Work", value: s.pending_tasks, icon: <AssignmentIcon fontSize="small" />, color: "info", link: "/my-desk" },
    { label: "Services", value: s.total_services, icon: <ListAltIcon fontSize="small" />, color: "success", link: "/services" },
  ],
  Manager: (s) => [
    { label: "On My Desk", value: s.on_my_desk, icon: <DesktopMacIcon fontSize="small" />, color: "primary", link: "/my-desk" },
    { label: "My Active", value: s.my_active, icon: <WorkIcon fontSize="small" />, color: "info", link: "/my-desk" },
    { label: "Team Working", value: s.team_under_me, icon: <GroupsIcon fontSize="small" />, color: "secondary", link: "/my-desk" },
    { label: "Due Today", value: s.due_today, icon: <EventAvailableIcon fontSize="small" />, color: "warning", link: "/my-desk" },
    { label: "Overdue", value: s.overdue, icon: <EventBusyIcon fontSize="small" />, color: "error", link: "/my-desk" },
  ],
  Team: (s) => [
    { label: "On My Desk", value: s.on_my_desk, icon: <DesktopMacIcon fontSize="small" />, color: "primary", link: "/my-desk" },
    { label: "My Active", value: s.my_active, icon: <AssignmentIcon fontSize="small" />, color: "info", link: "/my-desk" },
    { label: "Due Today", value: s.due_today, icon: <EventAvailableIcon fontSize="small" />, color: "warning", link: "/my-desk" },
    { label: "Overdue", value: s.overdue, icon: <EventBusyIcon fontSize="small" />, color: "error", link: "/my-desk" },
    { label: "Submitted", value: s.submitted, icon: <SendIcon fontSize="small" />, color: "success", link: "/my-desk" },
  ],
  Auditor: (s) => [
    { label: "Pending Audits", value: s.pending_audits, icon: <DesktopMacIcon fontSize="small" />, color: "primary", link: "/my-desk" },
    { label: "On My Desk", value: s.on_my_desk, icon: <AssignmentIcon fontSize="small" />, color: "info", link: "/my-desk" },
    { label: "Verified Today", value: s.verified_today, icon: <VerifiedIcon fontSize="small" />, color: "success", link: "/audit" },
    { label: "Rejected Today", value: s.rejected_today, icon: <CancelIcon fontSize="small" />, color: "error", link: "/audit" },
    { label: "Overdue", value: s.overdue, icon: <EventBusyIcon fontSize="small" />, color: "warning", link: "/my-desk" },
  ],
};

const subtitleByRole = {
  Admin: "Here's what's happening across your firm today.",
  BDM: "Your client pipeline & job overview.",
  Manager: "Your team's active work and deadlines.",
  Team: "Your active work and upcoming deadlines.",
  Auditor: "Files waiting for your verification.",
};

/* -------------------- QUICK ACTIONS -------------------- */
const quickActionsByRole = {
  Admin: [
    { label: "New Client", icon: <AddIcon fontSize="small" />, link: "/clients", color: "primary" },
    { label: "New Job", icon: <WorkIcon fontSize="small" />, link: "/jobs", color: "warning" },
    { label: "My Desk", icon: <DesktopMacIcon fontSize="small" />, link: "/my-desk", color: "info" },
  ],
  BDM: [
    { label: "New Client", icon: <AddIcon fontSize="small" />, link: "/clients", color: "primary" },
    { label: "New Job", icon: <WorkIcon fontSize="small" />, link: "/jobs", color: "warning" },
    { label: "My Desk", icon: <DesktopMacIcon fontSize="small" />, link: "/my-desk", color: "info" },
  ],
  Manager: [
    { label: "My Desk", icon: <DesktopMacIcon fontSize="small" />, link: "/my-desk", color: "primary" },
  ],
  Team: [
    { label: "My Desk", icon: <DesktopMacIcon fontSize="small" />, link: "/my-desk", color: "primary" },
  ],
  Auditor: [
    { label: "My Desk", icon: <DesktopMacIcon fontSize="small" />, link: "/my-desk", color: "primary" },
  ],
};

/* -------------------- HELPERS -------------------- */
const greet = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const formatDate = () => {
  const d = new Date();
  return d.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const initials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d)) return false;
  return d < new Date(new Date().toDateString());
};

/* -------------------- MAIN -------------------- */
export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role || "";
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [pendingJobs, setPendingJobs] = useState([]);
  const [error, setError] = useState("");

  const isAdminOrBDM = role === "Admin" || role === "BDM";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const calls = [api.get("/dashboard/stats.php")];
        if (isAdminOrBDM) calls.push(api.get("/dashboard/pending_jobs.php"));

        const results = await Promise.allSettled(calls);
        const s = results[0];
        if (s.status === "fulfilled") setStats(s.value.stats || {});

        if (isAdminOrBDM && results[1]) {
          const pj = results[1];
          if (pj.status === "fulfilled")
            setPendingJobs(pj.value.data || pj.value.jobs || []);
        }

        if (s.status === "rejected") setError("Failed to load dashboard");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [role, isAdminOrBDM]);

  const cards = (cardsByRole[role] || (() => []))(stats);
  const quickActions = quickActionsByRole[role] || [];

  return (
    <Box>
      {/* ========== HERO WELCOME BANNER ========== */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          color: "#fff",
          background:
            "linear-gradient(135deg, #4f46e5 0%, #6d28d9 50%, #9333ea 100%)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -60,
            right: -60,
            width: 180,
            height: 180,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.08)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -40,
            right: 100,
            width: 120,
            height: 120,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.06)",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 52,
              height: 52,
              fontWeight: 800,
              fontSize: 18,
              border: "2px solid rgba(255,255,255,0.3)",
              backdropFilter: "blur(10px)",
            }}
          >
            {initials(user?.name)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 0.25 }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: "#fff", lineHeight: 1.2 }}
              >
                {greet()}, {user?.name?.split(" ")[0] || "User"}
              </Typography>
              <Chip
                size="small"
                label={role}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  fontWeight: 700,
                  height: 20,
                  fontSize: 10,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Stack>
            <Typography
              variant="caption"
              sx={{ opacity: 0.9, display: "block", fontSize: 12 }}
            >
              {subtitleByRole[role] || "Welcome to your dashboard."}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 1, flexWrap: "wrap" }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarTodayIcon sx={{ fontSize: 12, opacity: 0.85 }} />
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, opacity: 0.9 }}
                >
                  {formatDate()}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AccessTimeIcon sx={{ fontSize: 12, opacity: 0.85 }} />
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, opacity: 0.9 }}
                >
                  {new Date().toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {quickActions.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexShrink: 0,
                position: "relative",
                zIndex: 1,
                flexWrap: "wrap",
              }}
            >
              {quickActions.map((a) => (
                <Button
                  key={a.label}
                  component={RouterLink}
                  to={a.link}
                  size="small"
                  startIcon={a.icon}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.18)",
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    fontSize: 12,
                    py: 0.5,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
                  }}
                >
                  {a.label}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ========== STAT CARDS ========== */}
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {cards.map((c, i) => (
          <Grid item xs={6} sm={4} md={cards.length === 5 ? 2.4 : 3} key={i}>
            <StatCard {...c} loading={loading} />
          </Grid>
        ))}
        {!loading && cards.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">No dashboard widgets for your role.</Alert>
          </Grid>
        )}
      </Grid>

      {/* ========== PENDING JOBS TABLE (Admin / BDM) ========== */}
      {isAdminOrBDM && (
        <Paper
          sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "#fff",
          }}
          elevation={0}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Avatar
              sx={{
                bgcolor: "warning.50",
                color: "warning.main",
                width: 32,
                height: 32,
              }}
            >
              <WorkIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Pending Jobs
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Jobs awaiting allocation or completion
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/jobs"
              size="small"
              endIcon={<ArrowForwardIcon fontSize="small" />}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              View all
            </Button>
          </Stack>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "text.secondary" }}>
                    Client
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "text.secondary" }}>
                    Service
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "text.secondary" }}>
                    Due Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "text.secondary" }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Skeleton />
                      <Skeleton />
                      <Skeleton />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && pendingJobs.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ py: 5, color: "text.secondary" }}
                    >
                      <Stack alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            bgcolor: "success.50",
                            color: "success.main",
                            width: 44,
                            height: 44,
                          }}
                        >
                          <VerifiedIcon />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          All caught up!
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          No pending jobs right now.
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  pendingJobs.map((j, idx) => {
                    const name = j.client_name || j.business_name || "—";
                    const overdue = isOverdue(j.due_date);
                    return (
                      <TableRow key={j.id || idx} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar
                              sx={{
                                bgcolor: "primary.50",
                                color: "primary.main",
                                width: 28,
                                height: 28,
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {initials(name)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {j.service_name || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: overdue ? "error.main" : "text.primary",
                                fontWeight: overdue ? 700 : 400,
                              }}
                            >
                              {j.due_date || "—"}
                            </Typography>
                            {overdue && (
                              <Tooltip title="Overdue">
                                <EventBusyIcon
                                  sx={{ color: "error.main", fontSize: 14 }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={j.status || "Pending"}
                            sx={{ fontWeight: 600, fontSize: 10, height: 20 }}
                            color={
                              (j.status || "")
                                .toLowerCase()
                                .includes("complete")
                                ? "success"
                                : "warning"
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {/* ========== HINT FOR NON-ADMIN ROLES ========== */}
      {!isAdminOrBDM && !loading && (
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, #eef2ff 0%, #f3e8ff 100%)",
          }}
          elevation={0}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Avatar
              sx={{
                bgcolor: "primary.main",
                width: 48,
                height: 48,
                boxShadow: "0 8px 16px rgba(79,70,229,0.3)",
              }}
            >
              <DesktopMacIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Your work is on My Desk
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All active files allocated to you are available on your desk.
                Open it to start working.
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/my-desk"
              variant="contained"
              size="small"
              startIcon={<DesktopMacIcon fontSize="small" />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                px: 2,
                background:
                  "linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #4338ca 0%, #5b21b6 100%)",
                },
              }}
            >
              Open My Desk
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
